import type Swiper from 'swiper';

import type { MediaAssetServiceCallback } from '@services/MediaAssetService';
import mediaAssetServiceInstance from '@services/MediaAssetService';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { frameDebounce } from '@utilities/throttle';

export type PluginOptions = {
  desktopCardFormat?: string;
  mobileCardFormat?: string;
  cardFormat?: string;
  category?: string;
  action?: string;
  label?: string;
  name?: string;
  more?: object;
  buttonDisabled?: boolean;
};

export default class PkCarouselPlugin {
  options: PluginOptions;
  dom: HTMLPkCarouselElement;
  instance: Swiper;
  arrowClicked: boolean;

  videoItems: HTMLElement[];
  videoIds: string[];
  cardItems: HTMLElement[];

  constructor(dom: HTMLElement, o: PluginOptions) {
    const defaults: PluginOptions = {
      mobileCardFormat: 'portrait',
    };

    this.options = {
      ...defaults,
      ...o,
    };

    this.dom = dom as HTMLPkCarouselElement;
  }

  async init() {
    this.instance = await this.dom.getSwiperInstance();
    this.setItems();

    this.instance?.on('slideNextTransitionEnd', this.onSlideNextTransitionEnd);
    this.instance?.on('slidePrevTransitionEnd', this.onSlidePrevTransitionEnd);
    this.instance?.on('slideChangeTransitionStart', this.onSlideChange);
    window.addEventListener('resize', this.setItems);
    window.addEventListener('orientationchange', this.setItems);

    this.initializeVideoHandling();

    if (mediaAssetServiceInstance.isActive) {
      this.videoItems = Array.from(this.dom.querySelectorAll<HTMLElement>('.js-video-item'));
      this.videoIds = this.videoItems.map((el) => el.dataset.videoid);
      this.videoIds.length && mediaAssetServiceInstance.on(this.videoIds, this._checkVideos, this);
    }

    const isMobile = screen.availWidth < 768;

    !isMobile && this.addSwipeListeners();
  }

  detach() {
    this.videoIds?.length && mediaAssetServiceInstance.off(this.videoIds, this._checkVideos, this);
    window.removeEventListener('resize', this.setItems);
    window.removeEventListener('orientationchange', this.setItems);
    this.removeSwipeListeners();
  }

  initializeVideoHandling() {
    const videoContainers = this.dom.querySelectorAll('.header-xl-item--video');
    videoContainers.forEach((videoContainer) => {
      videoContainer.addEventListener('click', (event) => {
        event.stopPropagation();
      });
    });
  }

  addSwipeListeners = () => {
    const videoContainers = this.dom.querySelectorAll<HTMLElement>('.header-xl-item--picture.header-xl-item--video');

    videoContainers.forEach((videoContainer) => {
      let startX: number;

      const onMouseDown = (event: MouseEvent) => {
        if (event.button === 0) {
          startX = event.clientX;
          event.preventDefault();
        }
      };

      const onMouseMove = (event: MouseEvent) => {
        const moveX = event.clientX - startX;
        if (Math.abs(moveX) > 30) {
          this.instance.slideTo(this.instance.activeIndex + (moveX > 0 ? -1 : 1));
          document.removeEventListener('mousemove', onMouseMove);
        }
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
      };

      videoContainer.addEventListener('mousedown', onMouseDown);
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });
  };

  removeSwipeListeners = () => {
    const videoContainers = this.dom.querySelectorAll<HTMLElement>('.header-xl-item--picture.header-xl-item--video');
    videoContainers.forEach((videoContainer) => {
      videoContainer.removeEventListener('mousedown', this.addSwipeListeners);
    });
  };

  _trackEvent = (direction: string) => {
    if (this.arrowClicked === true) {
      GoogleTagManagerHelper.event(
        null,
        null,
        null,
        {
          interaction: this.options.action,
          section_reference: `Swipe carousel - ${this.options.action} - ${direction}`,
        },
        'swipe_carousel',
      );
      this.arrowClicked = false;
    } else {
      GoogleTagManagerHelper.event(
        null,
        null,
        null,
        { interaction: this.options.action, section_reference: `Swipe carousel - ${this.options.action}` },
        'swipe_carousel',
      );
    }
  };

  onSlideNextTransitionEnd = () => {
    if (this.options.action) {
      this._trackEvent('Next');
    }
  };

  onSlidePrevTransitionEnd = () => {
    if (this.options.action) {
      this._trackEvent('Prev');
    }
  };

  onSlideChange = (swiper: Swiper) => {
    swiper.slides.forEach((slide, i) => {
      const player = slide?.querySelector('.jwplayer');
      if (!player) return;

      const jwPlayer = jwplayer(player);
      if (!jwPlayer) return;

      const isCurrentSlide = i === swiper.activeIndex;

      if (isCurrentSlide) {
        jwPlayer.on('fullscreen', this.handleVideoFullscreen);
      } else {
        jwPlayer.off('fullscreen', this.handleVideoFullscreen);
      }

      if (jwPlayer.getFullscreen()) return;

      const isPlaying = jwPlayer.getState() === 'playing';

      if (isCurrentSlide && !isPlaying) jwPlayer.play();
      else if (isPlaying) jwPlayer.pause();
    });
  };

  handleVideoFullscreen = (e: jwplayer.FullscreenParam) => {
    if (e.fullscreen && this.instance?.autoplay?.running) {
      this.instance.autoplay.pause();
    }

    if (!e.fullscreen && this.instance?.autoplay?.paused) {
      this.instance.autoplay.resume();
    }
  };

  _checkVideos: MediaAssetServiceCallback = (videos) => {
    let refresh = false;
    this.videoIds.forEach((id) => {
      const video = videos.find((v) => v.mediaAssetId === `${id}`);
      const available = video && video.status === 'AVAILABLE';
      const element = this.videoItems.find((e) => e.matches(`[data-videoid="${id}"]`));

      if (element.classList.contains('hidden') !== !available) {
        refresh = true;
        element.classList.toggle('hidden', !available);
      }
    });

    refresh && this.instance?.update();
  };

  setItems = frameDebounce(() => {
    const isMobile = screen.availWidth < 768;
    return Array.from(this.dom.querySelectorAll<HTMLElement>('.item')).map((item) => {
      if (this.options.desktopCardFormat) {
        if (isMobile) {
          item.classList.remove(`${this.options.desktopCardFormat}-card`);
          this.options.mobileCardFormat && item.classList.add(`${this.options.mobileCardFormat}-card`);
        } else {
          item.classList.remove(`${this.options.mobileCardFormat}-card`);
          this.options.desktopCardFormat && item.classList.add(`${this.options.desktopCardFormat}-card`);
        }
      } else if (this.options.cardFormat) {
        item.classList.add(`${this.options.cardFormat}-card`);
      }
    });
  });
}
